import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";

import { WaveformScrubber } from "@/components/tests/WaveformScrubber";

interface DictationPlayerProps {
  /** Resolves to the recording's bytes. Fetched on demand. */
  load: () => Promise<Blob>;
  /** Length in whole seconds, as the server measured it at upload.
   *
   *  Shown BEFORE the audio is fetched. Without it the row reads
   *  "0:00 / 0:00" until the reviewer presses play, which looks like an
   *  empty recording -- exactly the thing they are checking for. The
   *  audio element's own duration takes over once it loads, so a
   *  disagreement resolves towards the file itself. */
  durationHint?: number | null;
}

/** Playback speeds a reviewer actually wants.
 *
 *  Faster than 1x to get through forty recordings; slower to catch a
 *  word that was rushed. The browser's default `<audio controls>` gives
 *  play, pause and seek but no speed at all — which is the control that
 *  matters most when the job is listening to a lot of dictation. */
const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

/**
 * Listen to one candidate's dictation.
 *
 * Loads on first press rather than on mount: a review screen shows forty
 * cases, and fetching every recording up front would pull tens of
 * megabytes nobody has asked for.
 */
export function DictationPlayer({ load, durationHint }: DictationPlayerProps) {
  const [url, setUrl] = useState<string | null>(null);
  // The decoded bytes, kept so the scrubber can draw the recording's
  // actual shape. Held once and shared: fetching twice to draw a picture
  // would double the transfer for every recording a reviewer opens.
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(durationHint ?? 0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Release the blob when this row goes away, or it stays in memory for
  // the life of the page.
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, url]);

  const ensureLoaded = useCallback(async (): Promise<string | null> => {
    if (url) return url;
    setLoading(true);
    setError(false);
    try {
      const blob = await load();
      const next = URL.createObjectURL(blob);
      setUrl(next);
      // Read the bytes once, for the waveform. If this fails the audio
      // still plays -- the picture is the optional half.
      void blob
        .arrayBuffer()
        .then(setBytes)
        .catch(() => setBytes(null));
      return next;
    } catch {
      setError(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, [load, url]);

  const toggle = async () => {
    const src = await ensureLoaded();
    if (!src) return;
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.playbackRate = speed;
      await el.play().catch(() => setError(true));
    } else {
      el.pause();
    }
  };

  const restart = async () => {
    const src = await ensureLoaded();
    if (!src || !audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.playbackRate = speed;
    await audioRef.current.play().catch(() => setError(true));
  };

  const scrub = (value: number) => {
    if (audioRef.current) audioRef.current.currentTime = value;
    setPosition(value);
  };

  if (error) {
    return (
      <p data-cy="audio-error" className="mt-2 text-xs text-status-urgent">
        The recording could not be loaded.
      </p>
    );
  }

  return (
    <div
      data-cy="dictation-player"
      className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-border bg-background px-3 py-2"
    >
      <button
        type="button"
        data-cy="btn-play-pause"
        onClick={() => void toggle()}
        disabled={loading}
        aria-label={playing ? "Pause" : "Play"}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-text disabled:opacity-50"
      >
        {playing ? (
          <Pause className="h-4 w-4" aria-hidden />
        ) : (
          <Play className="h-4 w-4" aria-hidden />
        )}
      </button>

      <button
        type="button"
        data-cy="btn-restart"
        onClick={() => void restart()}
        disabled={loading}
        aria-label="Restart from the beginning"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-foreground-muted hover:border-accent hover:text-accent disabled:opacity-50"
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden />
      </button>

      {/* The waveform IS the scrubber. It falls back to a plain slider
          when the audio cannot be decoded -- losing the ability to seek
          because a picture failed would be a bad trade. */}
      <WaveformScrubber
        bytes={bytes}
        position={position}
        duration={duration}
        onSeek={scrub}
      />

      <span className="font-mono text-[11px] tabular-nums text-foreground-muted">
        {clock(position)} / {clock(duration)}
      </span>

      <label className="flex items-center gap-1 text-[11px] text-foreground-muted">
        Speed
        <select
          data-cy="audio-speed"
          value={speed}
          onChange={(e) => setSpeed(Number(e.target.value))}
          className="sk-input sk-sm w-auto"
        >
          {SPEEDS.map((s) => (
            <option key={s} value={s}>
              {s}×
            </option>
          ))}
        </select>
      </label>

      {loading && (
        <span className="text-[11px] text-foreground-muted">Loading…</span>
      )}

      {url && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audioRef}
          src={url}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            // A WAV recorded by MediaRecorder can report Infinity until
            // it has been seeked; treat that as unknown rather than
            // rendering "Infinity".
            setDuration(Number.isFinite(d) ? d : 0);
          }}
          hidden
        />
      )}
    </div>
  );
}

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}
